import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../hooks';
import {
  selectIsSwitchingTenantSession,
  selectSwitchingToTenantName,
} from '../../containers/Login/LoginSlice';
import {
  CLOSE_FADE_OUT_MS,
  MIN_VISIBLE_MS,
  SContent,
  SDetailMessage,
  SLeadMessage,
  SOverlay,
  SRing,
  SSpinnerBounds,
} from './TenantSwitchingOverlay.styles';

type Phase = 'hidden' | 'visible' | 'leaving';

/** Full-screen overlay shown while organization switching is in progress. */
export const TenantSwitchingOverlay = () => {
  const { t } = useTranslation();
  const visible = useAppSelector(selectIsSwitchingTenantSession);
  const switchingToTenantName = useAppSelector(selectSwitchingToTenantName);

  const [phase, setPhase] = useState<Phase>('hidden');
  const [frozenName, setFrozenName] = useState<string | null>(null);
  const shownAtRef = useRef(0);

  useEffect(() => {
    if (visible) {
      if (phase === 'visible') return;
      shownAtRef.current = Date.now();
      setFrozenName(switchingToTenantName ?? null);
      setPhase('visible');
      return;
    }

    if (phase === 'visible') {
      // Enforce a minimum visible window so fast switches don't flicker.
      const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAtRef.current));
      const timer = setTimeout(() => setPhase('leaving'), remaining);
      return () => clearTimeout(timer);
    }

    if (phase === 'leaving') {
      const timer = setTimeout(() => setPhase('hidden'), CLOSE_FADE_OUT_MS);
      return () => clearTimeout(timer);
    }
  }, [visible, phase, switchingToTenantName]);

  if (phase === 'hidden') return null;

  const trimmedName = frozenName?.trim() ?? '';
  const leadText = t('tenant_switching_lead_message');
  const detailText =
    trimmedName.length > 0
      ? t('tenant_switching_to_message', { tenant: trimmedName })
      : t('tenant_switching_message');

  return createPortal(
    <SOverlay role="status" aria-live="polite" aria-atomic="true" data-phase={phase}>
      <SContent>
        <SSpinnerBounds>
          <SRing />
        </SSpinnerBounds>
        <SLeadMessage>{leadText}</SLeadMessage>
        <SDetailMessage>{detailText}</SDetailMessage>
      </SContent>
    </SOverlay>,
    document.body
  );
};
